import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

import { formatBytes } from './categories'

// Column widths are shared by the standalone header row and every group's
// table, so the columns stay ruler-straight down the whole category even
// though each group is its own <table>.
const COL = {
  type: 56,
  objective: '30%',
  size: 88,
  action: 132,
}

// A file's type chip, colored by extension. The palette matches the category
// cards' tints so a mixed group scans by type at a glance.
const EXT_TINTS = {
  py: { color: '#2E7D32', background: '#E8F5E9' },
  pdf: { color: '#C4472F', background: '#FDEEEA' },
  txt: { color: '#4A5A7B', background: '#EDF0F7' },
  md: { color: '#4A5A7B', background: '#EDF0F7' },
  csv: { color: '#9A6C10', background: '#FBF3E2' },
}
const DEFAULT_TINT = { color: '#5F6B65', background: 'rgba(0, 0, 0, 0.06)' }

const useStyles = makeStyles(theme => ({
  group: {
    border: '1px solid #E3E8E4',
    borderRadius: 10,
    marginBottom: theme.spacing(1.5),
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    padding: theme.spacing(1.25, 1.75),
    border: 'none',
    background: '#FBFCFB',
    font: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#F3FAF3' },
    '&:focus': { outline: 'none' },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: 'inset 0 0 0 2px rgba(76, 175, 80, 0.55)',
    },
  },
  caret: {
    transition: 'transform 120ms',
    color: '#8A968F',
    '&[data-open="true"]': { transform: 'rotate(180deg)' },
  },
  badge: {
    minWidth: 28,
    padding: '3px 7px',
    borderRadius: 5,
    fontFamily: 'Menlo, Consolas, monospace',
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    textAlign: 'center',
    color: '#2E7D32',
    backgroundColor: '#E8F5E9',
    border: '1px solid #CDE8CE',
  },
  groupLabel: {
    flexGrow: 1,
    fontWeight: 600,
    letterSpacing: '0.01em',
  },
  groupMeta: {
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },

  // The column header sits once above the accordion rather than repeating
  // inside every open group. It is a table sharing the groups' colgroup and
  // cell padding: a flex row cannot land on the same column edges, because
  // percentage widths resolve against a different box and flex has no
  // equivalent of the cells' padding.
  headerTable: {
    // Each group's table sits inside a 1px border, so the header inset has to
    // match or every column starts a pixel early.
    margin: '0 1px',
    width: 'calc(100% - 2px)',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    marginBottom: theme.spacing(1.25),
  },
  headerCell: {
    padding: theme.spacing(0, 1.75, 1),
    borderBottom: '1px solid #E3E8E4',
    fontSize: '0.66rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#77837C',
    textAlign: 'left',
    verticalAlign: 'bottom',
    whiteSpace: 'nowrap',
  },
  headerCellRight: { textAlign: 'right' },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  row: {
    transition: 'background-color 120ms',
    '&:hover': { backgroundColor: '#F3FAF3' },
  },
  cell: {
    padding: theme.spacing(0.75, 1.75),
    borderTop: '1px solid #EEF1EE',
    verticalAlign: 'middle',
  },
  ext: {
    display: 'inline-block',
    width: 40,
    padding: '4px 0',
    borderRadius: 6,
    fontFamily: 'Menlo, Consolas, monospace',
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textAlign: 'center',
  },
  name: {
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  objective: {
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  size: {
    fontFamily: 'Menlo, Consolas, monospace',
    fontSize: '0.72rem',
    color: '#8A968F',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  actionCell: { textAlign: 'right' },
  // One fixed width for View, Download, and the fetching state, so the label
  // swap never resizes the button and adjacent rows line up.
  action: {
    width: 112,
    minWidth: 112,
    paddingLeft: 0,
    paddingRight: 0,
    position: 'relative',
    overflow: 'hidden',
  },
  '@keyframes sweep': {
    '0%': { left: '-45%' },
    '100%': { left: '100%' },
  },
  // Progress lives inside the button: an indeterminate sweep behind the
  // label, because the only phase we can observe is the URL mint — the
  // browser owns the transfer, so a percentage would be invented.
  sweep: {
    '&.Mui-disabled': { color: theme.palette.text.secondary },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: '45%',
      background:
        'linear-gradient(90deg, transparent, rgba(76, 175, 80, 0.35), transparent)',
      animation: '$sweep 1.1s ease-in-out infinite',
    },
  },
  sweepLabel: { position: 'relative' },
}))

/**
 * The column widths, shared by the header table and every group's table.
 *
 * Both are `table-layout: fixed` over the same content width, so identical
 * <col> widths put every column edge in the same place.
 */
function Columns() {
  return (
    <colgroup>
      <col style={{ width: COL.type }} />
      <col />
      <col style={{ width: COL.objective }} />
      <col style={{ width: COL.size }} />
      <col style={{ width: COL.action }} />
    </colgroup>
  )
}

/**
 * One category's groups, as an accordion of tables.
 *
 * `badge` and `meta` are supplied per group by the server, so the client just
 * renders them: code solutions arrive grouped by mission ("M6", "8 objectives")
 * and documents by their source subfolder, with no branch here.
 */
function ResourceGroups({ groups, busyPath, onOpenFile, onIntentFile }) {
  const classes = useStyles()
  const [collapsed, setCollapsed] = React.useState({})

  // Open the first group by default; the rest start closed. A bare group has
  // no header to reopen it with, so it is never closable.
  const isOpen = (group, index) =>
    group.bare || (collapsed[group.label] === undefined ? index === 0 : !collapsed[group.label])

  return (
    <div>
      <table className={classes.headerTable}>
        <Columns />
        <thead>
          <tr>
            <th className={classes.headerCell} />
            <th className={classes.headerCell} scope='col'>{'File'}</th>
            <th className={classes.headerCell} scope='col'>{'Objective'}</th>
            <th
              className={`${classes.headerCell} ${classes.headerCellRight}`}
              scope='col'
            >
              {'Size'}
            </th>
            <th className={classes.headerCell} />
          </tr>
        </thead>
      </table>

      {groups.map((group, index) => {
        const open = isOpen(group, index)
        return (
          <div className={classes.group} key={group.label}>
            {!group.bare &&
            <button
              type='button'
              className={classes.head}
              aria-expanded={open}
              onClick={() =>
                setCollapsed(prev => ({ ...prev, [group.label]: open }))
              }
            >
              <ExpandMoreIcon className={classes.caret} data-open={open} fontSize='small' />
              {group.badge && <span className={classes.badge}>{group.badge}</span>}
              <Typography variant='body2' className={classes.groupLabel}>
                {group.label}
              </Typography>
              <Typography
                variant='caption'
                color='textSecondary'
                className={classes.groupMeta}
              >
                {group.meta}
              </Typography>
            </button>}

            {open &&
              <table className={classes.table}>
                <Columns />
                <tbody>
                  {group.files.map((file) => {
                    const ext = (file.ext || '').toLowerCase()
                    const tint = EXT_TINTS[ext] || DEFAULT_TINT
                    const busy = busyPath === file.path
                    return (
                      <tr className={classes.row} key={file.path}>
                        <td className={classes.cell}>
                          <span className={classes.ext} style={tint}>
                            {ext.toUpperCase()}
                          </span>
                        </td>
                        <td className={classes.cell}>
                          <Typography variant='body2' className={classes.name} title={file.name}>
                            {file.name}
                          </Typography>
                        </td>
                        <td className={classes.cell}>
                          <Typography
                            variant='body2'
                            className={classes.objective}
                            title={file.subtitle || ''}
                          >
                            {file.subtitle || ''}
                          </Typography>
                        </td>
                        <td className={`${classes.cell} ${classes.size}`}>
                          {formatBytes(file.size)}
                        </td>
                        {/*
                          The label follows the server's `serve` field, never
                          the extension — so how a file is delivered can change
                          without a UI deploy.
                        */}
                        <td className={`${classes.cell} ${classes.actionCell}`}>
                          <Button
                            className={`${classes.action} ${busy ? classes.sweep : ''}`}
                            size='small'
                            variant={file.serve === 'download' ? 'contained' : 'outlined'}
                            color={file.serve === 'download' ? 'primary' : 'default'}
                            disabled={busy}
                            onClick={() => onOpenFile(file)}
                            onMouseEnter={() => onIntentFile && onIntentFile(file)}
                            onFocus={() => onIntentFile && onIntentFile(file)}
                          >
                            {busy ?
                              <span className={classes.sweepLabel}>{'Fetching…'}</span> :
                              (file.serve === 'download' ? 'Download' : 'View')}
                          </Button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>}
          </div>
        )
      })}
    </div>
  )
}

export { ResourceGroups }
